import React, { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Drawer,
  Heading,
  Button,
  Text,
  Divider,
  Tooltip,
  TextField,
  CardNotification,
  Loading,
  toast,
} from "@foris/avocado-suite";
import { useMutation } from "@apollo/client";
import { CubeMutation, GenericInvalidValidation, InvalidLabelLink } from "@models/ISchema";
import { LINK_CRUD_MUTATION } from "@dimensions/link/graphql/linkCrudMutation";
import { LINK_ERROR_LABELS } from "@models/Errors/LinkErrors/LinkErrors";
import { ILeague } from "@modules/vacancies/contextVacancies/ContextVacancies";
import { emitEvent } from "@common/hooks/useListenEvent";
import useContextUrl from "@common/hooks/useContextUrl";
import i18n from "@config/i18n";
import styles from "./linkClientCodeDrawer.module.scss";

interface LinkClientCodeDrawerProps {
  isOpen: boolean;
  linkDetails?: ILeague;
  onClose: () => void;
}

const getErrorMessage = (label: string, isValid: boolean) => {
  if (!label || isValid) {
    return "";
  }

  if (label?.length !== 2) {
    return i18n.t("bundle.create-link.forms.code-link-input.error-text");
  }

  return (
    <>
      {i18n.t("bundle.create-link.forms.code-link-input.tooltip.text-2-1")}
      (&#39; &quot;)
      {i18n.t("bundle.create-link.forms.code-link-input.tooltip.text-2-2")}
    </>
  );
};

const getMutationErrorMessage = (reason: string, params: any) => {
  if (!reason || !(reason in LINK_ERROR_LABELS)) {
    return null;
  }

  return LINK_ERROR_LABELS?.[reason](params);
};

const LinkClientCodeDrawer = ({ isOpen, linkDetails, onClose }: LinkClientCodeDrawerProps) => {
  const { origin, scenario, workspace } = useContextUrl();
  const [linkLabel, setLinkLabel] = useState("");
  const [isEditedAfterError, setIsEditedAfterError] = useState(false);
  const {
    t,
    i18n: { language },
  } = useTranslation();

  const [updateLinkLabel, { loading, data: mutationData }] = useMutation(LINK_CRUD_MUTATION, {
    onCompleted: data => {
      const cube = data?.cube as CubeMutation;

      if (cube?.linksCrud?.commited) {
        onClose?.();
        toast.success(t("bundle.create-link.notifications.success"), {
          autoClose: 3000,
        });
        emitEvent("on:refresh", {
          target: "bundle-view",
        });
      }
    },
  });

  const isLabelValid = /^(?!.*[\s\n'""])(?!.*\\n)[^\s\n'""]{2}$/.test(linkLabel);
  const errorMessage = getErrorMessage(linkLabel, isLabelValid);

  const mutationError = useMemo(() => {
    const error = (mutationData?.cube as CubeMutation)?.linksCrud?.payload?.updates?.[0]
      ?.validationErrors?.[0] as GenericInvalidValidation | InvalidLabelLink;
    const errorLink = !!error && "link" in error ? error?.link : undefined;
    const errorPayload = !!errorLink
      ? {
          link: errorLink,
          scenario,
          workspace,
          origin,
        }
      : undefined;

    return getMutationErrorMessage(error?.reason ?? "", errorPayload);
  }, [mutationData, language]);

  const handleUpdateLinkLabel = () => {
    setIsEditedAfterError(false);

    updateLinkLabel({
      variables: {
        originId: origin,
        scenarioId: scenario,
        input: {
          dryRun: false,
          skipValidations: false,
          changesets: {
            updates: [
              {
                linkId: linkDetails?.linkId,
                label: linkLabel,
              },
            ],
          },
        },
      },
    });
  };

  const selectedLeague = linkDetails?.code;
  const subject = linkDetails?.course?.name || "-";
  const campus = linkDetails?.course?.curriculum?.program?.campus?.code || "-";
  const period = linkDetails?.bundle?.term?.code || "-";

  return (
    <>
      {loading && <Loading />}

      <Drawer
        isOpen={isOpen}
        onClose={onClose}
        title={<Heading type="h3">{t("bundle.create-link.title")}</Heading>}
        closeOnOverlayClick
        footer={
          <div className={styles.drawerFooter}>
            <Button variant="ghost" onClick={onClose}>
              {t("bundle.create-link.forms.btn-cancel")}
            </Button>
            <Button
              disabled={!isLabelValid || loading || (!!mutationError && !isEditedAfterError)}
              onClick={handleUpdateLinkLabel}
            >
              {t("bundle.create-link.forms.btn-confirm")}
            </Button>
          </div>
        }
      >
        <section className={styles.clientCodeManagement}>
          <div className={styles.clientCodeManagement_informationBox}>
            <Heading className={styles.title} type="h4">
              {t("bundle.create-link.forms.information-box.title")}
            </Heading>

            <Text className={styles.selectedLeague} weight="medium" type="sm">
              {selectedLeague}
            </Text>

            <Text weight="medium" className={styles.subtitle} type="sm">
              {t("bundle.create-link.forms.information-box.subtitle.title")}
            </Text>

            <div className={styles.content}>
              <Text type="sm" className={styles.content_text}>
                {t("bundle.create-link.forms.information-box.subtitle.course")}:
              </Text>
              <Text weight="medium" type="sm" className={styles.content_bold}>
                {subject}
              </Text>

              <Divider vertical className={styles.content_divider} />
              <Text type="sm" className={styles.content_text}>
                {t("bundle.create-link.forms.information-box.subtitle.campus")}:
              </Text>

              <Text weight="medium" type="sm" className={styles.content_bold}>
                {campus}
              </Text>

              <Divider vertical className={styles.content_divider} />

              <Text type="sm" className={styles.content_text}>
                {t("bundle.create-link.forms.information-box.subtitle.period")}:
              </Text>

              <Text weight="medium" type="sm" className={styles.content_bold}>
                {period}
              </Text>
            </div>
          </div>

          <div className={styles.clientCodeManagement_control}>
            <Tooltip
              label={
                (
                  <ul className={styles.tooltipLabel}>
                    <li>{t("bundle.create-link.forms.code-link-input.tooltip.text-1")}</li>
                    <li>
                      {t("bundle.create-link.forms.code-link-input.tooltip.text-2-1")} (&#39;
                      &quot;), {t("bundle.create-link.forms.code-link-input.tooltip.text-2-2")}
                    </li>
                    <li>{t("bundle.create-link.forms.code-link-input.tooltip.text-3")}</li>
                  </ul>
                ) as any
              }
              placement="right"
            >
              <label className={styles.label} htmlFor="linkLabel">
                {t("bundle.create-link.forms.code-link-input.label")}
              </label>
            </Tooltip>

            <TextField
              id="linkLabel"
              className={styles.textField}
              placeholder={t("bundle.create-link.forms.code-link-input.placeholder")}
              value={linkLabel}
              status={!!errorMessage ? "error" : null}
              statusDisplay="inline"
              statusText={t("bundle.create-link.forms.code-link-input.error-text")}
              onChange={e => {
                setLinkLabel(e?.target?.value ?? "");

                if (!!mutationError && !isEditedAfterError) {
                  setIsEditedAfterError(true);
                }
              }}
            />

            {mutationError && !loading && !isEditedAfterError && (
              <CardNotification
                outlined
                state="error"
                className={styles.errorCard}
                title={mutationError?.title as string}
              >
                <Text type="sm">{mutationError?.message}</Text>
              </CardNotification>
            )}
          </div>
        </section>
      </Drawer>
    </>
  );
};

export default LinkClientCodeDrawer;
